import type { InventoryResource, Snapshot } from '@repo/api-gw-sdk';
import { ResourceType } from '@repo/api-gw-sdk';

import { Panels } from '@/components/panels/panels';
import type { PanelTypes } from '@/components/panels/panelsMap';

interface RestoreWizardArgs {
  snapshot: Snapshot;
  resource: InventoryResource;
  onClose: () => void;
  onBack: () => void;
}

export type RestoreWizardFactory = (args: RestoreWizardArgs) => PanelTypes;

type RestoreWizardConfig =
  | (typeof Panels)[keyof typeof Panels]
  | RestoreWizardFactory;

const restoreWizardConfigs: Partial<Record<ResourceType, RestoreWizardConfig>> =
  {
    [ResourceType.AwsS3]: Panels.RestoreS3Wizard,
    [ResourceType.AzureStorageAccount]: Panels.RestoreS3Wizard,
    [ResourceType.GcpCloudStorageBucket]: Panels.RestoreS3Wizard,
    [ResourceType.AwsRds]: Panels.RestoreRDSWizard,
    [ResourceType.AwsDynamoDb]: Panels.RestoreDynamoWizard,
    [ResourceType.AwsKeyspacesTable]: Panels.RestoreKeyspacesWizard,
    [ResourceType.AtlasMongodbCluster]: Panels.RestoreMongoAtlasWizard,
    [ResourceType.AwsEksNamespace]: Panels.RestoreEksNamespaceWizard,
    [ResourceType.AzureSqlManagedInstance]: Panels.RestoreAzureSqlDBWizard,
    [ResourceType.AzureSqlDatabase]: Panels.RestoreAzureSqlDBWizard,
    [ResourceType.AzurePostgresql]: Panels.RestoreAzurePostgresWizard,
    [ResourceType.AzureSqlVirtualMachine]: Panels.RestoreAzureVMWizard,
    [ResourceType.AzureSapHanaVm]: Panels.RestoreAzureVMWizard,
    [ResourceType.GcpSapHanaVm]: Panels.RestoreGcpVMWizard,
    [ResourceType.GcpCloudSqlInstance]: Panels.RestoreGcpCloudSqlWizard,
    [ResourceType.GcpBigQuery]: Panels.RestoreBigQueryWizard,
    [ResourceType.GcpDisk]: Panels.RestoreGcpDiskWizard,
    [ResourceType.AwsEbsVolume]: (args) => ({
      panel: Panels.RestoreVolumeWizard,
      props: {
        ...args,
        actionType: 'volume',
        providerVolumeId:
          args.snapshot.resource?.properties?.awsEbsVolume?.volume
            ?.providerVolumeId || '',
      },
    }),
  };

export const getRestoreWizardFactory = (
  resourceType: ResourceType
): RestoreWizardFactory | undefined => {
  const config = restoreWizardConfigs[resourceType];
  if (!config) return undefined;
  if (typeof config === 'function') return config;
  return (args) => ({ panel: config, props: args }) as PanelTypes;
};
